import { authStorage } from './authStorage';

export interface ApiErrorPayload {
  code?: string | number;
  message?: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string | number;
  readonly details?: unknown;
  constructor(status: number, message: string, code?: string | number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query) {
  const url = new URL(path.startsWith('/') ? `${BASE_URL}${path}` : `${BASE_URL}/${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  const pathWithQuery = url.pathname + (url.search || '');
  return BASE_URL.startsWith('http') ? url.toString() : pathWithQuery;
}

async function request<T>(method: string, path: string, opts: { query?: Query; body?: unknown } = {}): Promise<T> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
  }

  if (response.status === 401) {
    authStorage.clearToken();
    const current = window.location.pathname + window.location.search;
    if (!current.startsWith('/login') && !current.startsWith('/auth/callback')) {
      authStorage.setReturnTo(current);
      window.location.replace(`/login?returnTo=${encodeURIComponent(current)}`);
    }
    throw new ApiError(401, 'Not authenticated');
  }

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!response.ok) {
    const err = payload as ApiErrorPayload | null;
    throw new ApiError(
      response.status,
      err?.message || `Request failed (${response.status})`,
      err?.code,
      err?.details,
    );
  }

  return (payload as { data?: T } | null)?.data !== undefined
    ? ((payload as { data: T }).data)
    : (payload as T);
}

export const apiClient = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, { body, query }),
  put: <T>(path: string, body?: unknown, query?: Query) => request<T>('PUT', path, { body, query }),
  patch: <T>(path: string, body?: unknown, query?: Query) => request<T>('PATCH', path, { body, query }),
  delete: <T>(path: string, body?: unknown, query?: Query) => request<T>('DELETE', path, { body, query }),
};

export function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'Unable to reach the server. Check your connection.';
    if (err.status === 403) return "You don't have access to this action.";
    if (err.status === 404) return 'Not found.';
    if (err.status >= 500) return 'Server error — the team has been notified.';
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'An unexpected error occurred.';
}
